// src/pages/News.tsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Newspaper,
  TrendingUp,
  Award,
  Users,
  Bell,
  Mail,
  Send,
  Calendar as CalendarIcon,
  Tag,
  Eye
} from 'lucide-react';
import { PageHero } from '@/components/PageHero';
import { NewsCard } from '@/components/NewsCard';
import { news, CATEGORY_LABELS } from '@/data/mock/news';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const News = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Filter news
  const filteredNews = news.filter(item => {
    const matchesSearch = item.title[isRTL ? 'ar' : 'en']
      .toLowerCase()
      .includes(searchQuery.toLowerCase()) ||
      item.excerpt[isRTL ? 'ar' : 'en']
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories from the data (in first-seen order)
  const categoryKeys = Array.from(new Set(news.map(n => n.category)));
  const categories = [
    { id: 'all', label: { en: 'All News', ar: 'جميع الأخبار' }, count: news.length },
    ...categoryKeys.map(key => ({
      id: key,
      label: CATEGORY_LABELS[key] ?? { en: key, ar: key },
      count: news.filter(n => n.category === key).length,
    })),
  ];

  // Featured news (first 3)
  const featuredNews = news.slice(0, 3);
  const regularNews = news.slice(3);

  return (
    <div className="min-h-screen">
      {/* Enhanced Page Hero */}
      <PageHero
        title={t('nav.news')}
        subtitle={isRTL ? 'آخر الأخبار والتحديثات من أكاديمية تشامبيونز' : 'Latest News and Updates from Champions Academy'}
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.news') }
        ]}
      />

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto"
          >
            {[
              { 
                icon: Newspaper, 
                value: news.length.toString(), 
                label: { en: 'Total Articles', ar: 'مقال' } 
              },
              { 
                icon: TrendingUp, 
                value: '50K+', 
                label: { en: 'Total Views', ar: 'مشاهدة' } 
              },
              { 
                icon: Award, 
                value: '100+', 
                label: { en: 'Achievements', ar: 'إنجاز' } 
              },
              { 
                icon: Users, 
                value: '5K+', 
                label: { en: 'Subscribers', ar: 'مشترك' } 
              },
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -4 }}
                className="p-6 rounded-2xl bg-card/80 backdrop-blur-xl border border-border/50 hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <stat.icon className="h-8 w-8 text-primary mx-auto mb-3" />
                <div className="text-4xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-medium">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-12 bg-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            {/* Search Bar */}
            <div className="relative mb-8">
              <Search className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'} h-5 w-5 text-muted-foreground`} />
              <Input
                type="text"
                placeholder={isRTL ? "ابحث في الأخبار..." : "Search news..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${isRTL ? 'pr-12 text-right' : 'pl-12'} h-14 text-lg rounded-full border-2 focus:border-primary/50`}
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? "default" : "outline"}
                  size="lg"
                  onClick={() => setSelectedCategory(category.id)}
                  className="rounded-full px-6 group"
                >
                  <Tag className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {category.label[isRTL ? 'ar' : 'en']}
                  <span className={`${isRTL ? 'mr-2' : 'ml-2'} px-2 py-0.5 rounded-full text-xs ${
                    selectedCategory === category.id 
                      ? 'bg-primary-foreground/20' 
                      : 'bg-muted'
                  }`}>
                    {category.count}
                  </span>
                </Button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured News Section */}
      {selectedCategory === 'all' && searchQuery === '' && (
        <section className="py-20 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-grid-pattern opacity-5" />

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
                <Award className="h-5 w-5 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  {isRTL ? 'الأخبار المميزة' : 'Featured News'}
                </span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
                {isRTL ? 'أهم الأخبار' : 'Top Stories'}
              </h2>

              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {isRTL
                  ? 'أبرز وأهم أخبار أكاديمية تشامبيونز'
                  : 'The most important news from Champions Academy'
                }
              </p>
            </motion.div>

            {/* Featured News Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              {featuredNews.map((item, idx) => (
                <NewsCard
                  key={item.id}
                  slug={item.slug}
                  title={item.title[isRTL ? 'ar' : 'en']}
                  excerpt={item.excerpt[isRTL ? 'ar' : 'en']}
                  image={item.image}
                  date={item.date}
                  category={CATEGORY_LABELS[item.category]?.[isRTL ? 'ar' : 'en'] ?? item.category}
                  delay={idx * 0.1}
                  isRTL={isRTL}
                  featured={idx === 0}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All News Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          {/* Results Count */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-muted-foreground">
              {isRTL 
                ? `تم العثور على ${filteredNews.length} خبر`
                : `Found ${filteredNews.length} news articles`
              }
            </p>
          </motion.div>

          {/* News Grid */}
          {filteredNews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredNews.map((item, idx) => (
                <NewsCard
                  key={item.id}
                  slug={item.slug}
                  title={item.title[isRTL ? 'ar' : 'en']}
                  excerpt={item.excerpt[isRTL ? 'ar' : 'en']}
                  image={item.image}
                  date={item.date}
                  category={CATEGORY_LABELS[item.category]?.[isRTL ? 'ar' : 'en'] ?? item.category}
                  delay={idx * 0.05}
                  isRTL={isRTL}
                />
              ))}
            </div>
          ) : (
            // No Results
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-6">
                <Search className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="text-2xl font-bold mb-2">
                {isRTL ? 'لم يتم العثور على نتائج' : 'No results found'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {isRTL 
                  ? 'جرب البحث بكلمات مختلفة أو تصفح جميع الأخبار'
                  : 'Try different keywords or browse all news'
                }
              </p>
              <Button onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}>
                {isRTL ? 'إعادة تعيين البحث' : 'Reset Search'}
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Newsletter Subscription Section */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            backgroundPosition: ["0% 50%", "100% 50%"],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 bg-[length:200%_100%]"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="max-w-4xl mx-auto"
          >
            {/* Newsletter Card */}
            <div className="relative p-12 rounded-3xl bg-card/80 backdrop-blur-xl border-2 border-primary/30 shadow-2xl overflow-hidden">
              {/* Background Decoration */}
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.1, 0.2, 0.1],
                }}
                transition={{ duration: 8, repeat: Infinity }}
                className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl"
              />

              <div className="relative z-10 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/20 mb-6">
                  <Bell className="h-8 w-8 text-primary" />
                </div>

                <h2 className="text-3xl sm:text-4xl font-bold mb-4 gradient-text">
                  {isRTL ? 'اشترك في نشرتنا الإخبارية' : 'Subscribe to Our Newsletter'}
                </h2>

                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                  {isRTL
                    ? 'احصل على آخر الأخبار والتحديثات مباشرة إلى بريدك الإلكتروني'
                    : 'Get the latest news and updates directly to your inbox'
                  }
                </p>

                {/* Newsletter Form */}
                <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
                  <div className="relative flex-1">
                    <Mail className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'} h-5 w-5 text-muted-foreground`} />
                    <Input
                      type="email"
                      placeholder={isRTL ? "بريدك الإلكتروني" : "Your email address"}
                      className={`${isRTL ? 'pr-12 text-right' : 'pl-12'} h-14 text-base rounded-full border-2`}
                    />
                  </div>
                  <Button size="lg" className="btn-glow px-10 rounded-full">
                    <Send className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {isRTL ? 'اشترك' : 'Subscribe'}
                  </Button>
                </div>

                {/* Trust Indicators */}
                <div className="flex flex-wrap justify-center gap-6 mt-8 pt-8 border-t border-border">
                  {[
                    { icon: Users, text: isRTL ? '5000+ مشترك' : '5000+ Subscribers' },
                    { icon: CalendarIcon, text: isRTL ? 'أسبوعياً' : 'Weekly Updates' },
                    { icon: Award, text: isRTL ? 'محتوى حصري' : 'Exclusive Content' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <item.icon className="h-4 w-4 text-primary" />
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default News;
